import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import {
  IRedistributionGenericRule,
  IRedistributionGenericRulePayload,
} from '~/connect/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IRedistributionGenericRule>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  >;
  update?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    IRedistributionGenericRulePayload
  >;
};

export const useManageRedistributionGenericRule = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const endpoint = `/tenants/api/wtb/tenants/${tenantCode}/redistributions/generic-rule`;

  const getQuery = useQuery({
    queryKey: ConnectQueryKeys.redistributionGenericRule.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IRedistributionGenericRule>>(endpoint),
    ...options.get,
    enabled: Boolean(options.get?.enabled),
  });

  const updateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(endpoint, {
        method: 'PUT',
        body: JSON.stringify(variables),
      }),
    keysToInvalidateOnSuccess: [
      ConnectQueryKeys.redistributionGenericRule.base(),
    ],
    retry: false,
    ...options.update,
  });

  return restrictApi(options, {
    get: getQuery,
    update: updateMutation,
  });
};
